import { useTranslations } from 'next-intl'
import { ComponentType, useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import { Controller } from 'react-hook-form'
import type { ImageUploadingPropsType } from 'react-images-uploading'

import {
    DropDiv,
    StyledImageUploading,
    ClearAllButton,
    HeaderButtons,
    ImageTable,
} from '../../styled/image-uploading'
import toastStore from '../../../stores/toast-store'
import Table, { TTableRef } from '../../../table'
import { TableColumn } from '../../../table/types'
import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'

// CommonJS module: depending on the interop the component is `default` or `default.default`
const ReactImageUploading = dynamic<ImageUploadingPropsType>(
    () =>
        import('react-images-uploading').then(mod => {
            const component: any = mod.default
            return (component?.default ?? component) as ComponentType<ImageUploadingPropsType>
        }),
    { ssr: false }
)

export type TGalleryField<T> = TCommonFields<T> & {
    type: 'gallery'
    default?: ImageItem[]
    attributes?: string[]
    draggable?: boolean
    maxSize?: number
}

type TImage = {
    id: number
    index: number
    file?: File
    dataURL?: string
    name: string
    alt: string
    keywords: string
    path: string
    order_index: number
}

export type ImageItem = Partial<TImage>

type TImageUploadingProps = {
    onChange: (newValues: ImageItem[]) => void
    value?: ImageItem[]
    attributes?: string[]
    draggable: boolean
    maxSize?: number // in MB
    disabled?: boolean
}

export const ImageUploading = ({
    onChange,
    value,
    attributes,
    draggable,
    maxSize,
    disabled = false,
}: TImageUploadingProps) => {
    const tableRef = useRef<TTableRef<ImageItem>>(null)
    const hasImages = !!value?.length
    const t = useTranslations('form.image-uploading')

    const { updateToast } = toastStore

    const columns: TableColumn<ImageItem>[] = [
        {
            label: t('image'),
            accessor: 'dataURL',
            formatter: (value, row) => {
                // images from the backend have no dataURL, only a path
                if (!value) {
                    return (
                        <img
                            src={`${process.env.NEXT_PUBLIC_BACKEND_URL}/${row.path}`}
                            alt=""
                            width="100"
                        />
                    )
                }

                return <img src={value} alt="" width="100" />
            },
        },
        {
            label: t('name'),
            accessor: 'name',
            formatter: (value, row) => (
                <input
                    value={value}
                    disabled={disabled}
                    onChange={e =>
                        onChange(
                            tableRef.current!.setRow({
                                ...row,
                                name: e.target.value,
                            })
                        )
                    }
                />
            ),
        },
        {
            label: t('alt'),
            accessor: 'alt',
            formatter: (value, row) => (
                <input
                    value={value}
                    disabled={disabled}
                    onChange={e =>
                        onChange(
                            tableRef.current!.setRow({
                                ...row,
                                alt: e.target.value,
                            })
                        )
                    }
                />
            ),
        },
        {
            label: t('keywords'),
            accessor: 'keywords',
            formatter: (value, row) => (
                <textarea
                    value={value}
                    disabled={disabled}
                    onChange={e =>
                        onChange(
                            tableRef.current!.setRow({
                                ...row,
                                keywords: e.target.value,
                            })
                        )
                    }
                />
            ),
        },
        {
            label: t('order'),
            accessor: 'order_index',
            ...(!draggable
                ? {
                    formatter: (value, row) => (
                        <input
                            value={value}
                            disabled={disabled}
                            onChange={e =>
                                onChange(
                                    tableRef.current!.setRow({
                                        ...row,
                                        order_index: e.target.value,
                                    })
                                )
                            }
                        />
                    ),
                }
                : {}),
        },
    ].filter(
        field => attributes?.includes(field.accessor as string) ?? true
    ) as TableColumn<ImageItem>[]

    const refreshTable = () => tableRef.current?.refreshData()
    const getData = () => ({
        data: value?.map((item, index) => ({ ...item, index: index })) ?? [],
        total: value?.length ?? 0,
    })

    const canRefresh = useRef(false)
    useEffect(() => {
        if (canRefresh.current) {
            refreshTable()
            canRefresh.current = false
        }
    }, [value])

    return (
        <ReactImageUploading
            maxFileSize={maxSize ? maxSize * 1024 * 1024 : undefined}
            multiple
            value={value?.map((item, index) => ({ ...item, index: index })) ?? []}
            onError={(errors, files) => {
                if (errors?.maxFileSize && maxSize !== undefined) {
                    const maxSizeMB = maxSize * 1024 * 1024
                    const errorFiles = files!
                        .filter(file => (file?.file?.size ?? 0) > maxSizeMB)
                        .map(file => file.file)
                    updateToast({
                        type: 'error',
                        message: t('too-big-images', {
                            maxSize: maxSize,
                            images: errorFiles
                                .map(
                                    file =>
                                        `${file!.name} (${(file!.size! / 1024 / 1024).toFixed(2)} MB)`
                                )
                                .join(', '),
                        }),
                    })
                }
            }}
            onChange={(newValues, addUpdatedIndex) => {
                if (addUpdatedIndex !== undefined) {
                    for (const index of addUpdatedIndex) {
                        const itemsIndexed = newValues
                            .filter(item => item.order_index !== undefined)
                            .map(item => item.order_index)
                        newValues[index] = {
                            ...newValues[index],
                            index:
                                Math.max(
                                    ...[
                                        0,
                                        ...newValues
                                            .filter(item => item.index !== undefined)
                                            .map(item => item.index),
                                    ]
                                ) + 1,
                            name: newValues[index].file?.name,
                            order_index:
                                itemsIndexed.length > 0 ? Math.max(...itemsIndexed) + 1 : 1,
                        }
                    }
                }
                else {
                    newValues.sort((a, b) => a.order_index - b.order_index)
                    newValues.forEach((item, index) => (item.order_index = index + 1))
                }

                canRefresh.current = true
                onChange(newValues)
            }}
        >
            {({ onImageUpload, onImageRemoveAll, onImageRemove, isDragging, dragProps }) => (
                <StyledImageUploading>
                    <HeaderButtons>
                        <DropDiv
                            style={isDragging ? { color: 'red' } : undefined}
                            aria-disabled={disabled}
                            onClick={disabled ? undefined : onImageUpload}
                            {...(disabled ? {} : dragProps)}
                        >
                            {t('click-or-drop')}
                        </DropDiv>
                        <ClearAllButton
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                                onImageRemoveAll()
                                onChange([])
                            }}
                            color="red"
                            variant="soft"
                        >
                            {t('delete-all')}
                        </ClearAllButton>
                    </HeaderButtons>
                    {hasImages && (
                        <ImageTable>
                            <Table
                                ref={tableRef}
                                columns={columns}
                                data={
                                    value?.map((item, index) => ({
                                        ...item,
                                        index: index,
                                    })) ?? []
                                }
                                getData={getData}
                                actions={disabled ? [] : [
                                    {
                                        label: t('delete'),
                                        // rows are indexed by their position in the value (see getData)
                                        action: row => onImageRemove(row.index!),
                                    },
                                ]}
                                id="index"
                                hidePagination
                                draggable={draggable}
                            />
                        </ImageTable>
                    )}
                </StyledImageUploading>
            )}
        </ReactImageUploading>
    )
}

type TProps<T> = {
    rowField: TGalleryField<T>
    fieldName: string
    labelWidth?: string
    fieldItem?: any
}

const ImageUploadingDefault = <T,>({ rowField, fieldName, labelWidth, fieldItem }: TProps<T>) => {
    const { disabled, required } = useFieldState(rowField)
    const noLabel = rowField.label == ''

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className={`form-gallery ${noLabel ? 'form-no-label' : ''}`}
            hideLabel={noLabel}
        >
            <Controller
                name={fieldName}
                rules={getFieldRules(rowField, required)}
                defaultValue={fieldItem ?? rowField.default}
                render={({ field: { ref, onChange, ...rest } }) => (
                    <ImageUploading
                        {...rest}
                        onChange={value => {
                            onChange(value)
                            rowField.onChange?.(value)
                        }}
                        attributes={rowField.attributes}
                        draggable={rowField.draggable ?? true}
                        maxSize={rowField.maxSize}
                        disabled={disabled}
                    />
                )}
            />
        </FieldRow>
    )
}

export default ImageUploadingDefault
